import React, { createContext } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Box } from 'grommet'
import { DragDropContext } from 'react-beautiful-dnd'
import Column from '../components/Column'
import Banner from '../components/Banner'
import Description from '../components/Description'
import Loading from '../components/Loading'
import 'normalize.css'
import { allIssues, currentStandings, updateStandings } from '../api'
import { gutterSize, bannerHeight } from '../variables'
import JiraOps from '../components/JiraOps'

const ContentContainer = styled.div`
  display: flex;
  position: relative;
  top: ${bannerHeight}px;
  width: 100%;
  font-family: 'IBM Plex Sans', sans-serif;
`

const ListContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${gutterSize}px;
  width: 100%;
`

const AppContext = createContext()

const LoadingIndicator = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  justify-content: center;
  align-items: center;
`

class App extends React.Component {
  state = {}

  static propTypes = {
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        displayName: PropTypes.string,
      }),
    ),
    page: PropTypes.string,
  }

  constructor(props) {
    super(props)
    this.state = {
      loading: false,
    }
  }

  async componentDidMount() {
    const { page } = this.props
    this.setState({ loading: true }, async () => {
      const issues = await allIssues()
      const current = await currentStandings(page)

      this.setState({
        allIssues: issues,
        currentStandings: current,
        loading: false,
      })
    })
  }

  onDragEnd = async result => {
    const { destination, source, draggableId } = result

    // improper destination, or the issue is dragged but its position stays the same
    if (destination && destination.droppableId === source.droppableId && destination.index === source.index) {
      return
    }

    // data for the start and finish column
    const start = this.state.currentStandings[source && source.droppableId]
    const finish = this.state.currentStandings[destination && destination.droppableId]

    if (!start || !finish) {
      return
    }

    // create a new state obj
    const newState = {
      ...this.state,
    }

    // issue dropped in same container in which it started
    if (start === finish) {
      const issueIds = start.slice()

      issueIds.splice(source.index, 1)
      issueIds.splice(destination.index, 0, draggableId)

      const colName = source.droppableId

      newState.currentStandings[colName] = issueIds
    }

    // issue is being dragged to new container
    else {
      const startIds = start.slice()
      const finishIds = finish.slice()

      startIds.splice(source.index, 1)
      finishIds.splice(destination.index, 0, draggableId)

      const startColName = source.droppableId
      const finishColName = destination.droppableId

      newState.currentStandings[startColName] = startIds
      newState.currentStandings[finishColName] = finishIds
    }

    // if there is issueInfo (i.e. the description component is populated with info about a specific issue)
    // we need to recompute the current rank every time the dragEnd event is fired.
    const newRank = newState.currentStandings.ranked.findIndex(issue => issue === draggableId)

    newState.issueInfo = this.state.allIssues.find(issue => issue.issueId === draggableId)

    // findIndex returns -1 if it doesn't exist. This means that it's unranked
    newState.currentRank = newRank === -1 ? null : newRank

    updateStandings(newState.currentStandings, this.props.page)
    this.setState(newState)
  }

  handleInfoClick = e => {
    e.preventDefault()

    this.setState({
      issueInfo: null,
    })
  }

  handleIssueClick = e => {
    e.preventDefault()
    const id = e.currentTarget.getAttribute('data-uxd')

    const currentRank = this.state.currentStandings.ranked.findIndex(issue => issue === id)

    this.setState({
      currentRank: currentRank === -1 ? null : currentRank,
      issueInfo: this.state.allIssues.find(issue => issue.issueId === id),
    })
  }

  handlePushBottom = e => {
    e.preventDefault()
    const id = e.currentTarget.getAttribute('data-uxd')
    const newState = {
      ...this.state,
    }
    const ranked = newState.currentStandings.ranked.filter(el => el !== id)
    const unranked = newState.currentStandings.unranked.filter(el => el !== id)
    newState.currentStandings.ranked = [...ranked, id]
    newState.currentStandings.unranked = unranked
    updateStandings(newState.currentStandings, this.props.page)
    this.setState(newState)
  }

  renderLists() {
    if (!this.state.currentStandings || !this.state.currentStandings.ranked || !this.state.currentStandings.unranked) {
      return
    }
    const { currentStandings, allIssues, issueInfo } = this.state
    const { page, columns } = this.props

    const tiles = columns.map(({ name, displayName, position }, idx) => (
      <Column
        key={idx}
        info={issueInfo || null}
        title={displayName}
        id={name}
        issues={currentStandings[name]}
        allIssues={allIssues}
        position={position}
        handleIssueClick={this.handleIssueClick}
        handleButtonClick={this.handlePushBottom}
      />
    ))

    if (page === 'admin') {
      tiles.push(
        <div key={2}>
          <JiraOps />
        </div>,
      )
    } else {
      tiles.push(
        <Description
          key={2}
          info={this.state.issueInfo || null}
          rank={this.state.currentRank}
          handleInfoClick={this.handleInfoClick}
        />,
      )
    }

    return tiles
  }

  refetch = action => {
    const timeout = action === 'remove' ? 0 : 2000
    setTimeout(async () => {
      const { page } = this.props
      const issues = await allIssues()
      const current = await currentStandings(page)

      this.setState({
        allIssues: issues,
        currentStandings: current,
      })
    }, timeout)
  }

  render() {
    const { loading } = this.state

    return (
      <>
        {!loading && (
          <Box animation="fadeIn">
            <Banner handleInfoClick={this.handleInfoClick} />
            <AppContext.Provider value={{ refetch: this.refetch }}>
              <ContentContainer>
                <DragDropContext onDragEnd={this.onDragEnd}>
                  <ListContainer>{this.renderLists()}</ListContainer>
                </DragDropContext>
              </ContentContainer>
            </AppContext.Provider>
          </Box>
        )}
        {loading && (
          <LoadingIndicator>
            <Loading />
          </LoadingIndicator>
        )}
      </>
    )
  }
}

export { App, AppContext }
